import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { trackPageView } from '../lib/analytics';
import { useCMS } from '../hooks/useCMS';

export default function MediaCoverage() {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  useEffect(() => {
    trackPageView('/media-coverage');
  }, []);

  const { getValue, getHtml, loading: cmsLoading } = useCMS();

  const mediaImages = [
    {
      src: getValue('story.media.image1', '/media/media/media-coverage-01.jpg'),
      caption: getValue('story.media.caption1', '媒體報導 1'),
    },
    {
      src: getValue('story.media.image2', '/media/media/media-coverage-02.jpg'),
      caption: getValue('story.media.caption2', 'HKET 中小企 — 純天然辣上癮'),
    },
    {
      src: getValue('story.media.image3', '/media/media/media-coverage-03.jpg'),
      caption: getValue('story.media.caption3', '媒體報導 2'),
    },
    {
      src: getValue('story.media.image4', '/media/media/media-coverage-04.jpg'),
      caption: getValue('story.media.caption4', '蘋果日報、地產站 — 多媒體報道'),
    },
    {
      src: getValue('story.media.image5', '/media/media/media-coverage-05.jpg'),
      caption: getValue('story.media.caption5', '電視節目與美食創作'),
    },
    {
      src: getValue('story.media.image6', '/media/media/media-coverage-06.jpg'),
      caption: getValue('story.media.caption6', '藝人朋友支持'),
    },
    {
      src: getValue('story.media.image7', '/media/media/media-coverage-07.jpg'),
      caption: getValue('story.media.caption7', '東張西望等節目報道'),
    },
    {
      src: getValue('story.media.image8', '/media/media/media-coverage-08.jpg'),
      caption: getValue('story.media.caption8', '《創業精神》— Alex 的營商之道'),
    },
    {
      src: getValue('story.media.image9', '/media/media/media-coverage-09.jpg'),
      caption: getValue('story.media.caption9', '各界名人到訪支持'),
    },
    {
      src: getValue('story.media.image10', '/media/media/media-coverage-10.jpg'),
      caption: getValue('story.media.caption10', 'ViuTV 節目拍攝花絮'),
    },
    {
      src: getValue('story.media.image11', '/media/media/media-coverage-11.jpg'),
      caption: getValue('story.media.caption11', '電視節目廚房錄影'),
    },
    {
      src: getValue('story.media.image12', '/media/media/media-coverage-12.jpg'),
      caption: getValue('story.media.caption12', '節目花絮'),
    },
    {
      src: getValue('story.media.image13', '/media/media/media-coverage-13.jpg'),
      caption: getValue('story.media.caption13', '即場鑊氣爆炒示範'),
    },
    {
      src: getValue('story.media.image14', '/media/media/media-coverage-14.jpg'),
      caption: getValue('story.media.caption14', '電視節目嘉賓合影'),
    },
    {
      src: getValue('story.media.image15', '/media/media/media-coverage-15.jpg'),
      caption: getValue('story.media.caption15', '烹飪教室媒體拍攝'),
    },
    {
      src: getValue('story.media.image16', '/media/media/media-coverage-16.jpg'),
      caption: getValue('story.media.caption16', '與名人好友廚房聚會'),
    },
    {
      src: getValue('story.media.image17', '/media/media/media-coverage-17.jpg'),
      caption: getValue('story.media.caption17', '合作花絮'),
    },
    {
      src: getValue('story.media.image18', '/media/media/media-coverage-18.jpg'),
      caption: getValue('story.media.caption18', '媒體烹飪示範活動'),
    },
    {
      src: getValue('story.media.image19', '/media/media/media-coverage-19.jpg'),
      caption: getValue('story.media.caption19', '雜誌專題報道'),
    },
  ];

  if (cmsLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-500">載入中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-x1493-dark text-white py-20 md:py-28 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/media/media/1786030012_hkgspicyvalley_bg.jpeg')" }}
        />
        <div className="absolute inset-0 bg-x1493-dark/70" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <p className="text-x1493-secondary font-bold tracking-widest mb-4">媒體報道</p>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            dangerouslySetInnerHTML={getHtml('mediaCoverage.hero.title', '媒體報導與花絮')}
          />
          <p
            className="text-lg text-white/80 leading-relaxed max-w-2xl mx-auto"
            dangerouslySetInnerHTML={getHtml('mediaCoverage.hero.subtitle', '感謝各大媒體、電視節目同埋美食平台嘅報道，令更多人認識正宗港式辣粉。')}
          />
        </div>
      </section>

      {/* Media Coverage Masonry */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-3"
              dangerouslySetInnerHTML={getHtml('mediaCoverage.media.title', '媒體報道')}
            />
            <p
              className="text-stone-600 max-w-2xl mx-auto"
              dangerouslySetInnerHTML={getHtml('mediaCoverage.media.description', '感謝各媒體與節目團隊的報道。')}
            />
          </div>

          <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
            {mediaImages.map((item, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setLightboxImage(item.src)}
                className="block w-full break-inside-avoid rounded-2xl overflow-hidden group relative text-left"
              >
                <img
                  src={item.src}
                  alt={item.caption}
                  className="w-full h-auto object-cover transition duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition duration-300" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300">
                  <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full">
                    點擊放大
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition duration-300">
                  <p className="text-white font-medium text-sm leading-snug">
                    {item.caption}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            type="button"
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
            aria-label="關閉"
          >
            <X size={32} />
          </button>
          <img
            src={lightboxImage}
            alt="放大預覽"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
}
